/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Cancel, Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import { MILL_ORDERS } from "app/routes/constant";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { getOrderNo } from "app/redux/slice/invoiceSlice";
import { getProductList } from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";
import { getSupplierList } from "app/redux/slice/supplierSlice";
import {
  createMillOrder,
  getByIdMillOrder,
  updateMillOrder,
} from "app/redux/slice/millOrderSlice";
import { getMillPurchaseList } from "app/redux/slice/millPurchaseSlice";
import { istDateInput, istToday } from "app/helpers/istDate";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "250px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "10px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
});

const validationSchema = yup.object({
  supplierId: yup.object().required("Please select vendor").nullable(),
  millPurchaseId: yup
    .object()
    .required("Please select mill purchase no")
    .nullable(),
  orderNo: yup.string().required("Please enter order number."),
  orderDate: yup.string().required("Please enter date."),
  products: yup.array().of(
    yup.object().shape({
      productId: yup.object().required("Please select product").nullable(),
      variantId: yup.object().required("Please select variant").nullable(),
      quantity: yup
        .number()
        .typeError("Quantity must be a number")
        .required("Please enter quantity")
        .min(1),
    }),
  ),
});

const AddAndUpdateOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const supplierData = useSelector((state) => state?.supplier);
  const millPurchaseData = useSelector((state) => state?.millPurchase);
  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);
  const mobileView = useMediaQuery("(max-width:575px)");

  const formik = useFormik({
    initialValues: {
      supplierId: null,
      millPurchaseId: null,
      orderNo: "",
      gpNumber: "",
      orderDate: istToday(),
      products: [
        {
          productId: null,
          variantId: null,
          quantity: "",
        },
      ],
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      const productArr = [];

      values.products?.forEach((val) => {
        productArr.push({
          productId: val?.productId?._id,
          variantId: val?.variantId?._id,
          quantity: val.quantity,
        });
      });
      setSubmitting(true);
      const newInvoice = {
        supplierId: values?.supplierId?._id,
        millPurchaseId: values?.millPurchaseId?._id,
        orderNo: values?.orderNo,
        gpNumber: values?.gpNumber,
        date: values?.orderDate,
        products: productArr,
      };
      const response = await dispatch(
        id
          ? updateMillOrder({ id, data: newInvoice })
          : createMillOrder(newInvoice),
      ).unwrap();
      if (response.status === 200) {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "success",
        });
        navigate(MILL_ORDERS);
        resetForm();
      } else {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors } = formik;
  const productsLength = values?.products?.length;

  const handleChange = (index, field, value) => {
    let updatedFields = values.products;
    updatedFields[index][field] = value;
    setFieldValue("products", updatedFields);
  };
  const handleAddMore = () => {
    formik.setValues({
      ...formik.values,
      products: [...formik.values.products, {}],
    });
  };

  const handleDeleteField = (index) => {
    let updatedFields = values.products;
    updatedFields.splice(index, 1);
    setFieldValue("products", updatedFields);
  };

  useEffect(() => {
    if (id) {
      dispatch(getByIdMillOrder(id))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const millOrderDetail = response?.data?.data?.millOrderDetail;
            let productArr = [];
            setFieldValue("supplierId", millOrderDetail?.supplierId);
            setFieldValue("millPurchaseId", millOrderDetail?.millPurchaseId);
            setFieldValue("orderNo", millOrderDetail?.orderNo);
            setFieldValue("gpNumber", millOrderDetail?.gpNumber);
            setFieldValue(
              "orderDate",
              istDateInput(millOrderDetail?.date),
            );
            millOrderDetail?.products?.forEach((val) => {
              productArr.push({
                productId: val?.productId,
                variantId: val?.variantId,
                quantity: val.quantity,
              });
            });
            setFieldValue("products", productArr);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    if (!id) {
      dispatch(getOrderNo({ type: "millOrder" }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const newOrderNumber = response?.data?.data?.orderNo;
            setFieldValue("orderNo", newOrderNumber);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }

    dispatch(getMillPurchaseList({ field: "createdAt", sort: -1 }));
    dispatch(getProductList({ field: "name", sort: 1 }));
    dispatch(getVariantList({ field: "name", sort: 1 }));
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Mill" }));
  }, []);

  const { handleSubmit } = formik;
  return (
    <PageContainer heading={id ? "Update Order" : "Add Order"}>
      <Card>
        <CardContent>
          <FormikProvider value={formik}>
            <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
              <Grid container border={1}>
                <Grid item sm={3} xs={12} borderRight={{ xs: 0, sm: 1 }}>
                  <Grid>
                    <Grid px={1} pt={1} mb={1}>
                      <JumboAutoComplete
                        options={supplierData.list ?? []}
                        label="Vendor*"
                        size="small"
                        getOptionLabel={(option) => option?.name}
                        name={"supplierId"}
                        value={values?.supplierId}
                        onChange={(evt, value) => {
                          setFieldValue("supplierId", value);
                          setFieldValue("millPurchaseId", null);
                        }}
                        helperText={formik?.errors?.supplierId}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={2.5} xs={12} borderRight={{ xs: 0, sm: 1 }}>
                  <Grid>
                    <Grid px={1} pt={1} mb={1}>
                      <JumboAutoComplete
                        options={
                          values?.supplierId
                            ? millPurchaseData?.list.filter(
                                (list) =>
                                  list?.supplier?._id ===
                                    values?.supplierId?._id &&
                                  list?.remainingQty > 0,
                              ) || []
                            : []
                        }
                        label="Purchase No*"
                        size="small"
                        getOptionLabel={(option) => option?.purchaseNumber}
                        name={"millPurchaseId"}
                        value={values?.millPurchaseId}
                        onChange={(evt, value) => {
                          setFieldValue("millPurchaseId", value);
                          setFieldValue(
                            `products[${0}].quantity`,
                            value?.remainingQty,
                          );
                          setFieldValue(
                            `products[${0}].productId`,
                            value?.product,
                          );
                        }}
                        helperText={formik?.errors?.millPurchaseId}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={6.5} xs={12}>
                  <Grid container>
                    <Grid
                      item
                      sm={4}
                      xs={12}
                      borderRight={{ xs: 0, sm: 1 }}
                      p={1}
                    >
                      <TextField
                        label={"Order No."}
                        name="orderNo"
                        fullWidth
                        size="small"
                        value={values?.orderNo}
                        onChange={(evt) =>
                          setFieldValue("orderNo", evt.target.value)
                        }
                        error={errors?.orderNo}
                        helperText={errors?.orderNo}
                      />
                    </Grid>
                    <Grid
                      item
                      sm={4}
                      xs={12}
                      borderRight={{ xs: 0, sm: 1 }}
                      p={1}
                    >
                      <TextField
                        label={"Date"}
                        name="orderDate"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(evt) =>
                          setFieldValue("orderDate", evt.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values?.orderDate}
                        error={errors?.orderDate}
                        helperText={errors?.orderDate}
                      />
                    </Grid>
                    <Grid item sm={4} xs={12} p={1}>
                      <TextField
                        label={"GP No."}
                        name="gpNumber"
                        fullWidth
                        size="small"
                        value={values?.gpNumber}
                        onChange={(evt) =>
                          setFieldValue("gpNumber", evt.target.value)
                        }
                        error={errors?.gpNumber}
                        helperText={errors?.gpNumber}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                borderRight={1}
                sx={{
                  mt: { xs: 3, md: 0 },
                }}
                className="mobile-view-table"
              >
                <TableContainer component={Paper}>
                  <Table
                    sx={{ minWidth: 650 }}
                    size="small"
                    aria-label="a dense table"
                    className={classes?.table}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Sr. No.</TableCell>
                        <TableCell className={classes?.particular}>
                          Product
                        </TableCell>
                        <TableCell className={classes?.particular}>
                          Variant
                        </TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {productsLength &&
                        values?.products?.map((item, index) => {
                          // sum += item?.amount ? +item?.amount : 0;
                          return (
                            <TableRow>
                              {!mobileView ? (
                                <TableCell>{index + 1}</TableCell>
                              ) : (
                                ""
                              )}
                              <TableCell className={classes?.particular}>
                                <JumboAutoComplete
                                  disabled
                                  options={productData.list ?? []}
                                  label="Product*"
                                  size="small"
                                  getOptionLabel={(option) => option?.name}
                                  name={`products[${index}].productId`}
                                  value={item?.productId}
                                  onChange={(evt, value) => {
                                    handleChange(index, "productId", value);
                                    handleChange(index, "variantId", null);
                                  }}
                                  helperText={
                                    formik?.errors?.products?.length &&
                                    formik?.errors?.products[index]?.productId
                                  }
                                />
                              </TableCell>
                              <TableCell className={classes?.particular}>
                                <JumboAutoComplete
                                  options={
                                    item?.productId
                                      ? variantData?.list.filter(
                                          (list) =>
                                            list?.product?._id ===
                                            item?.productId?._id,
                                        ) || []
                                      : []
                                  }
                                  label="Variant*"
                                  size="small"
                                  getOptionLabel={(option) => option?.name}
                                  name={`products[${index}].variantId`}
                                  value={item?.variantId}
                                  onChange={(evt, value) =>
                                    handleChange(index, "variantId", value)
                                  }
                                  helperText={
                                    formik?.errors?.products?.length &&
                                    formik?.errors?.products[index]?.variantId
                                  }
                                />
                              </TableCell>

                              <TableCell
                                sx={{ minWidth: "150px", maxWidth: "150px" }}
                              >
                                <TextField
                                  // disabled
                                  label="Qty"
                                  placeholder="Qty"
                                  name={`products[${index}].quantity`}
                                  fullWidth
                                  type="text"
                                  value={item?.quantity}
                                  size="small"
                                  onChange={(evt) =>
                                    handleChange(
                                      index,
                                      "quantity",
                                      +evt.target.value,
                                    )
                                  }
                                  error={
                                    errors?.products?.length &&
                                    errors?.products[index]?.quantity
                                  }
                                  helperText={
                                    errors?.products?.length &&
                                    errors?.products[index]?.quantity
                                  }
                                />
                              </TableCell>

                              <TableCell>
                                {index !== 0 && (
                                  <IconButton
                                    aria-label="delete"
                                    color="secondary"
                                    onClick={() => handleDeleteField(index)}
                                  >
                                    <Cancel />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid container spacing={3}>
                <Grid
                  item
                  sm={12}
                  xs={12}
                  className={classes?.loadingButtonContainer}
                >
                  <LoadingButton
                    type="submit"
                    buttonLabel={id ? "Update" : "Save"}
                    sx={{ marginRight: "10px" }}
                    startIcon={<Save />}
                    variant="outlined"
                    fullWidth={mobileView}
                  />
                  <LoadingButton
                    color={"secondary"}
                    buttonLabel={"Close"}
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => navigate(MILL_ORDERS)}
                    fullWidth={mobileView}
                  />
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default AddAndUpdateOrder;
